import type { ConnectionSummary } from '@/shared/api/generated/catalog';

/* "Unmeasured datasets, by source" — the arithmetic, kept out of the rendering so the absence rules
 * are testable without a browser.
 *
 * **Where the numbers come from, and why no new endpoint was needed.** `GET
 * /v1/catalog/connections/summary` already returns, per source, `datasets` and `scored` — one
 * statement the database aggregates, bounded rows on the wire, constant in the size of the estate.
 * So the unmeasured count is `datasets - scored` per row and the whole section is **one request**,
 * shared with the source catalogue through React Query's cache. A per-source or per-dataset read
 * would be the N+1 `HomeScreen`'s own docstring names as the thing a dashboard is exactly where
 * somebody writes.
 *
 * **The bar's length is the source's share of the largest unmeasured count, not of its own
 * catalogue.** The section's question is *which source is holding the most unmeasured tables*, so
 * the bars have to be comparable to each other; a bar showing each source's internal coverage would
 * make a source with 3 of 3 unmeasured look worse than one with 900 of 5,000. The copy beside the
 * bar states the count, so nothing here is the only place a number appears — the rule `Spark.tsx`
 * holds, for the same reason.
 *
 * **Four readings, and collapsing any two of them puts a false statement on screen.** This is the
 * same distinction `scoredCoverage` draws in `catalog-browser`, drawn again because this section
 * subtracts where that one divides — so `allScored` becomes a case that one does not have:
 *
 * * `notConsulted` — `scored` is null, so scoring was not asked. Subtracting from null would report
 *   the source's *whole* catalogue as unmeasured, which is a claim about a customer's estate made by
 *   a read that did not happen.
 * * `nothingCatalogued` — no datasets, so there is nothing to measure yet. Not a finding.
 * * `allScored` — every dataset has a score. **This is the case a zero-width bar would ruin**: an
 *   empty track is indistinguishable from a source we could not read, and this is the one row that
 *   is genuinely good news.
 * * `unmeasured` — a real count, and the only reading that draws a bar.
 */

/** A source's name, or why there is not one.
 *
 * The four kinds, and the copy keys they render through, are `catalog-browser`'s `ConnectionLabel` —
 * see `model/grouping.ts`, where the reasoning for keeping the three absent cases apart is written
 * out. Declared again here rather than imported because that type is internal to that feature and
 * ESLint's `boundaries/entry-point` rule forbids reaching past its barrel; the honest home for this
 * vocabulary is `entities/connection`, which already owns `useConnectionNames` and sits below both
 * features. **Recorded as the follow-up rather than done**, because it moves another feature's file.
 * What is deliberately *not* duplicated is the copy: both render through
 * `catalog.group.{loading,unreadable,unlisted}`, so the two screens cannot come to say different
 * things about the same absence.
 */
export type SourceName =
  | { readonly kind: 'named'; readonly name: string }
  | { readonly kind: 'loading' }
  | { readonly kind: 'unreadable' }
  | { readonly kind: 'unlisted' };

/** What the connection-name read is currently doing. Carried separately from the map because an
 * empty map means "still loading", "the read failed" and "no connections exist" — three facts, and
 * only the last is about the tenant. */
export interface SourceNames {
  readonly status: 'loading' | 'unreadable' | 'ready';
  readonly names: ReadonlyMap<string, string>;
}

export function sourceName(connectionId: string, lookup: SourceNames): SourceName {
  const name = lookup.names.get(connectionId);
  if (name !== undefined) return { kind: 'named', name };
  if (lookup.status === 'loading') return { kind: 'loading' };
  if (lookup.status === 'unreadable') return { kind: 'unreadable' };
  /* The read succeeded and this id was not in it. The connection listing caps at 200 with no
   * cursor, so on a large estate this is the cap rather than evidence the source was deleted — and
   * the copy must not claim the stronger thing. */
  return { kind: 'unlisted' };
}

export type UnmeasuredReading =
  | { readonly kind: 'notConsulted' }
  | { readonly kind: 'nothingCatalogued' }
  | { readonly kind: 'allScored'; readonly datasets: number }
  | { readonly kind: 'unmeasured'; readonly count: number; readonly datasets: number };

export function unmeasuredReading(summary: ConnectionSummary): UnmeasuredReading {
  /* Checked before the dataset count, because `scored === null` means the figure is unknown whether
   * or not there are datasets — and a source with no datasets *and* no scoring read is more
   * honestly reported as unread than as empty. */
  if (summary.scored === null) return { kind: 'notConsulted' };
  if (summary.datasets === 0) return { kind: 'nothingCatalogued' };
  /* `Math.max` rather than a bare subtraction: `scored` counts DISTINCT datasets carrying a score
   * while `datasets` counts undeleted ones, so a dataset scored and then retired could in principle
   * put `scored` ahead. A negative count would render as a bar pointing the wrong way; reading it
   * as "all scored" is the safe direction and the closer of the two to the truth. */
  const count = Math.max(0, summary.datasets - summary.scored);
  if (count === 0) return { kind: 'allScored', datasets: summary.datasets };
  return { kind: 'unmeasured', count, datasets: summary.datasets };
}

export interface UnmeasuredRow {
  readonly connectionId: string;
  readonly name: SourceName;
  readonly reading: UnmeasuredReading;
  /** 0..1 of the widest bar on the card, or `null` when this row draws no bar at all. */
  readonly share: number | null;
}

/** The narrowest bar a positive count may draw.
 *
 * Without a floor, one unmeasured dataset beside a source holding nine hundred renders as a
 * zero-width fill — a positive finding quantised to nothing, which is migration 0048's rule
 * ("refuse a positive rate that would quantise to zero") applied to a rendering, and the same call
 * `share` makes when it reports `<0.1%` rather than `0.0%`. 3% of the track is a few pixels at this
 * card's width: visible as a mark, and still obviously the smallest bar in the column.
 */
export const MIN_VISIBLE_SHARE = 0.03;

/* Rows worth acting on first, then rows that need a person for a different reason, then good news.
 *
 * `notConsulted` sorts above `allScored` deliberately: "we did not find out" is an unanswered
 * question and "everything here is scored" is an answer, so the unanswered one is what somebody
 * scanning the card should meet first. */
const READING_ORDER: readonly UnmeasuredReading['kind'][] = [
  'unmeasured',
  'notConsulted',
  'allScored',
  'nothingCatalogued',
];

/** How many unmeasured datasets a row is holding, or 0 for a row that has no count at all. Used
 * only as a sort term, which is why the three countless readings collapse here and nowhere else. */
function countOf(reading: UnmeasuredReading): number {
  return reading.kind === 'unmeasured' ? reading.count : 0;
}

/** Every source the summary read returned, as bars.
 *
 * **No source is filtered out**, and that is the absence rule rather than a completeness instinct:
 * a "by source" breakdown that silently omitted the fully-scored and the unread ones would leave a
 * reader unable to tell a source that is fine from a source this card did not cover — the
 * silent-gap failure, on a card whose subject is coverage.
 *
 * A pure sort over a copy, because this runs over query data React Query owns and sorting in place
 * would reorder the cache.
 */
export function unmeasuredBySource(
  summaries: readonly ConnectionSummary[],
  lookup: SourceNames,
): readonly UnmeasuredRow[] {
  const readings = summaries.map((summary) => ({
    summary,
    reading: unmeasuredReading(summary),
  }));

  const widest = readings.reduce(
    (most, entry) => Math.max(most, countOf(entry.reading)),
    0,
  );

  const rows: UnmeasuredRow[] = readings.map(({ summary, reading }) => ({
    connectionId: summary.connection_id,
    name: sourceName(summary.connection_id, lookup),
    reading,
    share:
      reading.kind === 'unmeasured' && widest > 0
        ? Math.max(MIN_VISIBLE_SHARE, reading.count / widest)
        : null,
  }));

  return rows.sort(
    (left, right) =>
      READING_ORDER.indexOf(left.reading.kind) -
        READING_ORDER.indexOf(right.reading.kind) ||
      countOf(right.reading) - countOf(left.reading) ||
      /* A stable tiebreaker, so two equal rows cannot swap places between renders. The same
       * reasoning `orderAttention` carries: a list whose order moves under the reader is one they
       * cannot work down. */
      left.connectionId.localeCompare(right.connectionId),
  );
}

/** The sentence under the bars.
 *
 * **Derived from the same rows the bars are, so the card cannot contradict itself.** The estate KPI
 * above computes its own scored/total pair from `GET /v1/scoring/estate` and the catalogue walk, and
 * those two reads scope datasets differently — so quoting an estate-wide total here would put two
 * figures for one quantity on one screen, which costs a reader their trust in both. This total is
 * explicitly "across the sources listed here", and the copy says so.
 *
 * `notConsulted` is counted rather than folded in, because a source scoring was not asked about
 * makes both figures **lower bounds**. Reporting "105 of 517" while two sources went unread would be
 * a complete-looking answer built from a partial read.
 */
export interface UnmeasuredTotal {
  readonly unmeasured: number;
  /** Datasets in the sources whose scored count *was* read. Not the estate total. */
  readonly datasets: number;
  /** Sources whose scored count came back null, so neither figure covers them. */
  readonly notConsulted: number;
}

export function unmeasuredTotal(
  summaries: readonly ConnectionSummary[],
): UnmeasuredTotal {
  let unmeasured = 0;
  let datasets = 0;
  let notConsulted = 0;
  for (const summary of summaries) {
    const reading = unmeasuredReading(summary);
    if (reading.kind === 'notConsulted') {
      notConsulted += 1;
      continue;
    }
    if (reading.kind === 'nothingCatalogued') continue;
    datasets += reading.datasets;
    unmeasured += countOf(reading);
  }
  return { unmeasured, datasets, notConsulted };
}
